import { useState } from "react";
import { Link } from "react-router";
import { Badge, Card, Icon, PageHeader, StatCard } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import type { AdminMetrics } from "@nelurio/contracts";
import { useAdminResource, useSystemState } from "../hooks";
import { ErrorState, HealthDot, LoadingState, healthText, healthTone } from "../components";
import { relativeDe } from "../notifications";
import type { AdminNotificationRow, SubscriptionStats } from "../types";

function formatBytes(bytes: number, formatNumber: (n: number) => string): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  if (bytes <= 0) return `${formatNumber(0)} B`;
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${formatNumber(value)} ${units[i]}`;
}

export function OverviewPage() {
  const { t, formatNumber, formatDate } = useI18n();
  const metrics = useAdminResource<AdminMetrics>("/metrics");
  const series = useAdminResource<{ series: { day: string; active: number }[] }>("/metrics-series");
  const [financeDays, setFinanceDays] = useState(30);
  const abos = useAdminResource<SubscriptionStats>(`/subscriptions/stats?days=${financeDays}`);
  const system = useSystemState();
  const sys = system.status === "ready" ? system.data : null;
  const activity = useAdminResource<AdminNotificationRow[]>("/notifications?limit=6");
  const eur = (cents: number) => formatNumber(cents / 100, { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
  const filterChips: Array<{ label: string; days: number }> = [
    { label: t("admin.finance.filter7"), days: 7 },
    { label: t("admin.finance.filter30"), days: 30 },
    { label: t("admin.finance.filter90"), days: 90 },
    { label: t("admin.finance.filterAll"), days: 0 }
  ];

  // Wix-Studio "Next Steps": every step is a REAL integration state — done
  // only when the connection is actually configured, never faked.
  const setupSteps = [
    { key: "stepAI", done: Boolean(sys?.integrations?.ai) },
    { key: "stepPush", done: Boolean(sys?.integrations?.push) },
    { key: "stepSMTP", done: Boolean(sys?.integrations?.smtp) },
    { key: "stepStorage", done: Boolean(sys?.integrations?.storage) },
    { key: "stepOAuth", done: Boolean(sys?.integrations?.oauth) },
    { key: "stepPayments", done: Boolean(abos.data?.paymentsActive) }
  ];
  const doneCount = setupSteps.filter((step) => step.done).length;

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.overview")} subtitle={t("admin.overview.subtitle")} />

      {metrics.status === "error" && <ErrorState message={metrics.error} onRetry={() => void metrics.reload()} />}
      {metrics.status === "loading" && <LoadingState rows={4} />}

      {metrics.status === "ready" && metrics.data && (
        <>
          <div className="admin-stats">
            <StatCard label={t("admin.totalUsers")} value={formatNumber(metrics.data.users.total)} icon="users" tone="purple" />
            <StatCard label={t("admin.activeToday")} value={formatNumber(metrics.data.users.activeToday)} icon="check" tone="green" />
            <StatCard label={t("admin.active7d")} value={formatNumber(metrics.data.users.active7d)} note={t("admin.active30d")} icon="calendar" tone="blue" />
            <StatCard label={t("admin.newUsers")} value={formatNumber(metrics.data.users.new7d)} icon="plus" tone="amber" />
          </div>

          <div className="admin-studio-grid">
            {/* Setup checklist (Wix "Next Steps" model, real integration states). */}
            <Card className="admin-panel studio-checklist">
              <h2>{t("admin.studio.setupTitle")}</h2>
              <div className="studio-checklist__progress" role="progressbar" aria-valuenow={doneCount} aria-valuemin={0} aria-valuemax={setupSteps.length}>
                <i style={{ width: `${Math.round((doneCount / setupSteps.length) * 100)}%` }} />
              </div>
              <small className="studio-checklist__count">{t("admin.studio.setupCount", { done: formatNumber(doneCount), total: formatNumber(setupSteps.length) })}</small>
              <div className="studio-checklist__rows">
                {setupSteps.map((step) => (
                  <div className="studio-checklist__row" key={step.key}>
                    <i className={`studio-checklist__dot ${step.done ? "is-done" : ""}`} aria-hidden="true"><Icon name={step.done ? "check" : "plus"} size={13} /></i>
                    <span>{t(`admin.studio.${step.key}`)}</span>
                    {step.done ? (
                      <Badge tone="green">{t("admin.studio.doneChip")}</Badge>
                    ) : (
                      <Link className="studio-checklist__open" to={`/setup?step=${step.key.replace("step", "").toLowerCase()}`}>{t("admin.studio.openStep")}</Link>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Recent activity (the same real event feed as the bell). */}
            <Card className="admin-panel studio-activity">
              <h2>{t("admin.studio.activityTitle")}</h2>
              <div className="studio-activity__rows">
                {activity.status === "ready" && (activity.data ?? []).length === 0 && (
                  <p className="nlp-hint">{t("admin.studio.activityEmpty")}</p>
                )}
                {activity.status === "ready" && (activity.data ?? []).map((item) => (
                  <div className="studio-activity__row" key={item.id}>
                    <strong className="admin-wrap">{item.title}</strong>
                    <span className="admin-wrap">{item.body}</span>
                    <small>{relativeDe(t, item.createdAt)}</small>
                  </div>
                ))}
                {activity.status === "loading" && <LoadingState rows={2} />}
                {activity.status === "error" && <ErrorState message={activity.error} onRetry={() => void activity.reload()} />}
              </div>
            </Card>
          </div>

          <div className="admin-overview-grid">
            <Card className="admin-chart admin-chart--real">
              <h2>{t("admin.overview.activePerDay")}</h2>
              {(() => {
                const pts = series.data?.series ?? [];
                if (pts.length === 0) {
                  return <p className="nlp-hint">{t("admin.overview.chartEmpty")}</p>;
                }
                const max = Math.max(1, ...pts.map((p) => p.active));
                return (
                  <div className="real-bars" role="img" aria-label={t("admin.overview.chartAriaLabel", { count: pts.length })}>
                    {pts.map((p) => (
                      <div key={p.day} className="real-bar-col" title={t("admin.overview.barTitle", { day: p.day, count: p.active })}>
                        <span className="real-bar-val">{formatNumber(p.active)}</span>
                        <i className="real-bar" style={{ height: `${Math.max(6, Math.round((p.active / max) * 160))}px` }} />
                        <span className="real-bar-day">{formatDate(p.day, { day: "numeric", month: "short" })}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Card>
            <Card className="admin-panel">
              <h2>{t("admin.overview.resources")}</h2>
              <div className="admin-panel__rows">
                <div className="admin-panel__row">
                  <span><i className="admin-panel__glyph is-blue"><Icon name="file" size={15} /></i>{t("admin.overview.materialsTotal")}</span>
                  <strong>{formatNumber(metrics.data.resources.total)}</strong>
                </div>
                <div className="admin-panel__row">
                  <span><i className="admin-panel__glyph is-amber"><Icon name="timer" size={15} /></i>{t("admin.overview.processing")}</span>
                  <Badge tone={metrics.data.resources.processing > 0 ? "amber" : "green"}>{formatNumber(metrics.data.resources.processing)}</Badge>
                </div>
                <div className="admin-panel__row">
                  <span><i className="admin-panel__glyph is-coral"><Icon name="warning" size={15} /></i>{t("admin.overview.failed")}</span>
                  <Badge tone={metrics.data.resources.failed > 0 ? "coral" : "neutral"}>{formatNumber(metrics.data.resources.failed)}</Badge>
                </div>
                <div className="admin-panel__row">
                  <span><i className="admin-panel__glyph is-purple"><Icon name="library" size={15} /></i>{t("admin.overview.storageUsed")}</span>
                  <strong>{formatBytes(metrics.data.resources.bytesStored, formatNumber)}</strong>
                </div>
              </div>
            </Card>

            <Card className="admin-panel">
              <h2>{t("admin.overview.finance")}</h2>
              <div className="admin-filter" role="group" aria-label={t("admin.finance.filterAria")}>
                {filterChips.map((chip) => (
                  <button
                    key={chip.days}
                    type="button"
                    className={`admin-filter__chip${financeDays === chip.days ? " is-active" : ""}`}
                    aria-pressed={financeDays === chip.days}
                    onClick={() => setFinanceDays(chip.days)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {abos.status === "ready" && abos.data ? (
                <div className="admin-panel__rows">
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-green"><Icon name="check" size={15} /></i>{t("admin.finance.earnedRow")}</span>
                    <strong>{eur(abos.data.earnedCents)}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-purple"><Icon name="credit-card" size={15} /></i>{t("admin.finance.mrrRow")}</span>
                    <strong>{eur(abos.data.mrrCents)}{t("admin.finance.perMonth")}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-blue"><Icon name="plus" size={15} /></i>{t("admin.finance.windowRow")}</span>
                    <strong>{formatNumber(abos.data.windowNew)} · +{eur(abos.data.windowMrrCents)}{t("admin.finance.perMonth")}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-purple"><Icon name="credit-card" size={15} /></i>{t("admin.overview.subsTotal")}</span>
                    <strong>{formatNumber(abos.data.total)}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-blue"><Icon name="timer" size={15} /></i>{t("admin.overview.activeTrials")}</span>
                    <strong>{formatNumber(abos.data.trial)}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-green"><Icon name="star" size={15} /></i>{t("admin.overview.premiumActive")}</span>
                    <strong>{formatNumber(abos.data.premium)}</strong>
                  </div>
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-amber"><Icon name="warning" size={15} /></i>{t("admin.overview.endingIn7d")}</span>
                    <Badge tone={abos.data.expiringSoon > 0 ? "amber" : "neutral"}>{formatNumber(abos.data.expiringSoon)}</Badge>
                  </div>
                  {abos.data.perApp.length > 0 && (
                    <>
                      <div className="admin-panel__row admin-finance-perapp-title"><span>{t("admin.finance.perAppTitle")}</span></div>
                      {abos.data.perApp.map((app) => (
                        <div className="admin-panel__row" key={app.appSlug}>
                          <span className="admin-wrap">{app.appSlug} · {formatNumber(app.premiumActive)} × Premium</span>
                          <strong>{eur(app.mrrCents)}{t("admin.finance.perMonth")}</strong>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : abos.status === "error" ? (
                <ErrorState message={abos.error} onRetry={() => void abos.reload()} />
              ) : (
                <LoadingState rows={2} />
              )}
              <p className="nlp-hint">{abos.data?.paymentsActive ? t("admin.overview.subsHint") : t("admin.finance.paymentsPending")}</p>
            </Card>

            <Card className="admin-panel">
              <h2>{t("admin.system")}</h2>
              <div className="admin-panel__rows">
                <div className="admin-panel__group"><span>{t("admin.overview.servicesGroup")}</span></div>
                {(["api", "database", "worker"] as const).map((service) => {
                  const status = metrics.data?.system[service] ?? "unknown";
                  return (
                    <div className="admin-panel__row" key={service}>
                      <span><HealthDot status={status} /><span className="admin-health-name">{service === "api" ? "API" : service === "database" ? t("admin.overview.serviceDatabase") : t("admin.overview.serviceWorker")}</span></span>
                      <Badge tone={healthTone(status)}>{healthText(t, status)}</Badge>
                    </div>
                  );
                })}
                {/* Real host + database readings, not just colors. */}
                <div className="admin-panel__group"><span>{t("admin.overview.resourcesGroup")}</span></div>
                {sys?.host && (
                  <>
                    <div className="admin-panel__row">
                      <span><i className="admin-panel__glyph is-blue"><Icon name="server" size={15} /></i>{t("admin.overview.ramRow")}</span>
                      <strong>{formatNumber(sys.host.memUsedMb / 1024, { maximumFractionDigits: 1 })} / {formatNumber(sys.host.memTotalMb / 1024, { maximumFractionDigits: 1 })} GB · {formatNumber(sys.host.memPercent, { maximumFractionDigits: 0 })}%</strong>
                    </div>
                    <div className="admin-panel__row">
                      <span><i className="admin-panel__glyph is-purple"><Icon name="file" size={15} /></i>{t("admin.overview.diskRow")}</span>
                      <strong>{formatNumber(sys.host.diskUsedGb, { maximumFractionDigits: 1 })} / {formatNumber(sys.host.diskTotalGb, { maximumFractionDigits: 1 })} GB · {formatNumber(sys.host.diskPercent, { maximumFractionDigits: 0 })}%</strong>
                    </div>
                  </>
                )}
                {sys?.dbSizeBytes ? (
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-green"><Icon name="library" size={15} /></i>{t("admin.overview.dbSizeRow")}</span>
                    <strong>{formatBytes(sys.dbSizeBytes, formatNumber)}</strong>
                  </div>
                ) : null}
                {sys?.dbLatencyMs ? (
                  <div className="admin-panel__row">
                    <span><i className="admin-panel__glyph is-amber"><Icon name="timer" size={15} /></i>{t("admin.overview.dbLatencyRow")}</span>
                    <strong>{formatNumber(sys.dbLatencyMs, { maximumFractionDigits: 1 })} ms</strong>
                  </div>
                ) : null}
              </div>
            </Card>
          </div>

          <section className="admin-section-title" aria-label={t("admin.overview.aiTodayAria")}>
            <h2>{t("admin.overview.aiToday")}</h2>
            <small>{t("admin.overview.aiTodayHint")}</small>
          </section>
          <div className="admin-stats">
            <StatCard label={t("admin.overview.aiRequests")} value={formatNumber(metrics.data.ai.requestsToday)} note={t("admin.overview.aiErrorRate", { rate: formatNumber(metrics.data.ai.errorRate) })} icon="brain" tone="purple" />
            <StatCard label={t("admin.overview.aiInputTokens")} value={formatNumber(metrics.data.ai.inputTokensToday)} icon="route" tone="blue" />
            <StatCard label={t("admin.overview.aiOutputTokens")} value={formatNumber(metrics.data.ai.outputTokensToday)} icon="sparkle" tone="green" />
            <StatCard label={t("admin.overview.aiCost")} value={formatNumber(metrics.data.ai.estimatedCostToday)} icon="chart" tone="amber" />
          </div>
        </>
      )}
    </div>
  );
}
